import React, { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Button,
  ColorPicker,
  Drawer,
  Form,
  Input,
  InputNumber,
  Spin,
  message
} from "antd";
import { CloseOutlined } from "@ant-design/icons";
import {
  createSprStatus,
  fetchSprStatus,
  saveSprStatus
} from "./spr-status-api";
import {
  colorHexToInt,
  colorIntToHex,
  emptyStatus,
  normalizeStatus
} from "./spr-status-utils";
import "./spr-statuses.css";

const DRAWER_WIDTH_KEY = "planOnline.sprStatus.cardDrawer.width";
const MIN_WIDTH = 420;
const MAX_WIDTH = 720;
const DEFAULT_WIDTH = 480;
const SCREEN_MARGIN = 100;

const maxAllowedWidth = () => {
  if (typeof window === "undefined") return MAX_WIDTH;
  return Math.min(MAX_WIDTH, Math.max(MIN_WIDTH, window.innerWidth - SCREEN_MARGIN));
};

const clampWidth = (value) => {
  const max = maxAllowedWidth();
  const n = Number(value);
  if (!Number.isFinite(n)) return Math.min(DEFAULT_WIDTH, max);
  return Math.min(max, Math.max(MIN_WIDTH, Math.round(n)));
};

const loadWidth = () => {
  try {
    return clampWidth(localStorage.getItem(DRAWER_WIDTH_KEY) ?? DEFAULT_WIDTH);
  } catch {
    return DEFAULT_WIDTH;
  }
};

const saveWidth = (value) => {
  try {
    localStorage.setItem(DRAWER_WIDTH_KEY, String(clampWidth(value)));
  } catch {
    /* ignore */
  }
};

/**
 * Карточка статуса (drawer + форма). Delphi: TFrmESprStatus.
 */
const SprStatus = ({ open, idStatus, isNew = false, canEdit = true, onClose, onSaved }) => {
  const [form] = Form.useForm();
  const [size, setSize] = useState(loadWidth);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) return undefined;
    const onWinResize = () => setSize((prev) => clampWidth(prev));
    onWinResize();
    window.addEventListener("resize", onWinResize);
    return () => window.removeEventListener("resize", onWinResize);
  }, [open]);

  const onResize = useCallback((next) => {
    setSize(clampWidth(next));
  }, []);

  const onResizeEnd = useCallback(
    (finalSize) => {
      const next = clampWidth(typeof finalSize === "number" ? finalSize : size);
      setSize(next);
      saveWidth(next);
    },
    [size]
  );

  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;

    if (isNew) {
      setLoading(false);
      setError("");
      const empty = emptyStatus();
      form.setFieldsValue({
        ...empty,
        color: colorIntToHex(empty.color)
      });
      return () => {
        cancelled = true;
      };
    }

    if (idStatus == null) return undefined;

    setLoading(true);
    setError("");
    fetchSprStatus(idStatus)
      .then((row) => {
        if (cancelled) return;
        const card = normalizeStatus(row);
        form.setFieldsValue({
          ...card,
          color: colorIntToHex(card.color)
        });
      })
      .catch((e) => {
        if (!cancelled) setError(e.message || "Не удалось загрузить статус");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [open, isNew, idStatus, form]);

  const onFinish = async (values) => {
    if (!canEdit) {
      onClose?.();
      return;
    }
    setSaving(true);
    setError("");
    const body = {
      idx: values.idx ?? 0,
      name: (values.name || "").trim(),
      color: colorHexToInt(values.color)
    };
    try {
      const saved = isNew
        ? await createSprStatus(body)
        : await saveSprStatus({ ...body, idStatus });
      message.success(isNew ? "Статус создан" : "Статус сохранён");
      onSaved?.(saved);
    } catch (e) {
      setError(e.message || "Не удалось сохранить");
    } finally {
      setSaving(false);
    }
  };

  const title = isNew
    ? "Новый статус"
    : `Статус${idStatus != null ? ` №${idStatus}` : ""}`;

  return (
    <Drawer
      placement="right"
      title={title}
      size={size}
      maxSize={maxAllowedWidth()}
      open={open}
      onClose={onClose}
      destroyOnClose
      closable={false}
      rootClassName="spr-status-drawer"
      extra={
        <Button
          type="text"
          className="spr-status-drawer__close"
          icon={<CloseOutlined />}
          aria-label="Закрыть"
          title="Закрыть"
          onClick={onClose}
        />
      }
      styles={{
        body: { display: "flex", flexDirection: "column", paddingTop: 12 }
      }}
      resizable={{
        onResize,
        onResizeEnd
      }}
    >
      {open && (idStatus != null || isNew) ? (
        <Spin spinning={loading}>
          {error ? (
            <Alert
              className="spr-statuses__error"
              type="error"
              showIcon
              message={error}
              style={{ marginBottom: 12 }}
            />
          ) : null}
          <Form
            form={form}
            layout="vertical"
            className="spr-status-drawer__form"
            disabled={!canEdit || loading || saving}
            onFinish={onFinish}
          >
            {!isNew ? (
              <Form.Item label="Код" name="idStatus">
                <InputNumber disabled style={{ width: "100%" }} />
              </Form.Item>
            ) : null}
            <Form.Item label="Порядок" name="idx">
              <InputNumber min={0} style={{ width: "100%" }} />
            </Form.Item>
            <Form.Item
              label="Наименование"
              name="name"
              rules={[{ required: true, message: "Укажите наименование" }]}
            >
              <Input />
            </Form.Item>
            <Form.Item
              label="Цвет"
              name="color"
              getValueFromEvent={(c) => (c?.toHexString ? c.toHexString() : c)}
              getValueProps={(v) => ({ value: v || "#000000" })}
            >
              <ColorPicker showText disabled={!canEdit || loading || saving} />
            </Form.Item>
            <div className="spr-status-drawer__footer">
              {canEdit ? (
                <Button type="primary" htmlType="submit" loading={saving}>
                  Сохранить
                </Button>
              ) : null}
              <Button onClick={onClose} disabled={saving}>
                Отмена
              </Button>
            </div>
          </Form>
        </Spin>
      ) : null}
    </Drawer>
  );
};

export default SprStatus;
